import React, { useEffect, useState } from 'react';
import { Alert, Avatar, Box, Button, Container, FormControl, InputLabel, MenuItem, Paper, Select, TextField, Typography } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';

const types = [
  ['custom', 'Custom content'],
  ['selected_vatus', 'Selected Vatu photos'],
  ['featured_photos', 'Featured photos'],
  ['top_performers', 'Top performers'],
  ['exam_batches', 'Exam activities'],
  ['antyakshari_batches', 'Antyakshari activities']
];

const blankSlide = () => ({ id: `slide-${Date.now()}`, type: 'custom', title: '', description: '', image: '', link: '', limit: 5, selected_vatu_ids: [], enabled: true });

const HomepageSlideshowConfig = () => {
  const { user } = useAuth();
  const [config, setConfig] = useState(null);
  const [slides, setSlides] = useState([]);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [vatus, setVatus] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        const response = await api.get('/api/admin-config');
        const result = await response.json();
        const active = result.data?.find(item => item.active);
        setConfig(active || null);
        setSlides(active?.homepage_slides || []);
        const vatuResponse = await api.get('/api/vatu/slideshow/vatus');
        const vatuResult = await vatuResponse.json();
        if (vatuResult.success) setVatus(vatuResult.data || []);
      } catch (loadError) {
        setError(loadError.message);
      }
    };
    load();
  }, []);

  const updateSlide = (index, field, value) => {
    setSlides(current => current.map((slide, slideIndex) => slideIndex === index ? { ...slide, [field]: value } : slide));
  };

  const save = async () => {
    if (!config) return;
    setMessage('');
    setError('');
    try {
      const response = await api.put(`/api/admin-config/${config._id}`, { homepage_slides: slides.map((slide, index) => ({ ...slide, order: index })) });
      const result = await response.json();
      if (!response.ok || !result.success) throw new Error(result.message || 'Unable to save slideshow configuration');
      setMessage('Homepage slideshow saved.');
    } catch (saveError) {
      setError(saveError.message);
    }
  };

  if (!user || !['Admin', 'SuperUser'].includes(user.role)) return <Container><Alert severity="error" sx={{ mt: 4 }}>You are not authorized to configure the homepage slideshow.</Alert></Container>;
  if (!config) return <Container><Alert severity="info" sx={{ mt: 4 }}>Create and activate an admin year configuration before adding homepage slides.</Alert></Container>;

  return (
    <Container maxWidth="md" sx={{ py: 3 }}>
      <Typography variant="h4" sx={{ color: '#7f4b2d', fontWeight: 700, mb: 1 }}>Homepage slideshow</Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>Slides rotate automatically every 2 seconds. Keep only the slides you want visible on the home page.</Typography>
      {message && <Alert severity="success" sx={{ mb: 2 }}>{message}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {slides.map((slide, index) => (
        <Paper key={slide.id || index} sx={{ p: { xs: 2, sm: 3 }, mb: 2 }} variant="outlined">
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Slide type</InputLabel>
              <Select value={slide.type} label="Slide type" onChange={event => updateSlide(index, 'type', event.target.value)}>
                {types.map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField label="Title" value={slide.title || ''} onChange={event => updateSlide(index, 'title', event.target.value)} />
            <TextField label="Description" value={slide.description || ''} onChange={event => updateSlide(index, 'description', event.target.value)} multiline minRows={2} />
            {slide.type === 'custom' && <TextField label="Image URL (custom slides)" value={slide.image || ''} onChange={event => updateSlide(index, 'image', event.target.value)} />}
            {slide.type === 'selected_vatus' && (
              <FormControl fullWidth>
                <InputLabel>Vatu photos</InputLabel>
                <Select
                  multiple
                  value={slide.selected_vatu_ids || []}
                  label="Vatu photos"
                  renderValue={selected => selected.map(id => vatus.find(vatu => vatu.vatu_id === id)?.vatu_name || id).join(', ')}
                  onChange={event => updateSlide(index, 'selected_vatu_ids', event.target.value)}
                >
                  {vatus.map(vatu => (
                    <MenuItem key={vatu.vatu_id} value={vatu.vatu_id}>
                      <Avatar src={vatu.photo_download_link || undefined} alt="" sx={{ width: 32, height: 32, mr: 1 }} />
                      {vatu.vatu_name} {vatu.pathasala_name ? `- ${vatu.pathasala_name}` : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <TextField label="Link (optional)" value={slide.link || ''} onChange={event => updateSlide(index, 'link', event.target.value)} />
            <TextField label="Items to show" type="number" inputProps={{ min: 1, max: 20 }} value={slide.limit || 5} onChange={event => updateSlide(index, 'limit', Number(event.target.value))} />
          </Box>
          <Button color="error" startIcon={<DeleteOutlineIcon />} sx={{ mt: 2 }} onClick={() => setSlides(current => current.filter((_, slideIndex) => slideIndex !== index))}>Remove slide</Button>
        </Paper>
      ))}
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Button startIcon={<AddIcon />} variant="outlined" onClick={() => setSlides(current => [...current, blankSlide()])}>Add slide</Button>
        <Button variant="contained" onClick={save}>Save slideshow</Button>
      </Box>
    </Container>
  );
};

export default HomepageSlideshowConfig;
